// AI Coding
/**
 * @file useConfig.ts
 * @description React Query hooks đọc cấu hình hệ thống (private, primary và public primary).
 */
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { configurationService } from "@/services/configuration.service";
import type { Configuration, PublicConfiguration } from "@/types/configuration";

export const configurationQueryKeys = {
  all: ["configurations"] as const,
  list: () => [...configurationQueryKeys.all, "list"] as const,
  primary: () => [...configurationQueryKeys.all, "primary"] as const,
  publicPrimary: () =>
    [...configurationQueryKeys.all, "public-primary"] as const,
};

export const useConfigurations = (enabled = true) => {
  return useQuery<Configuration[], Error>({
    queryKey: configurationQueryKeys.list(),
    queryFn: () => configurationService.getAll(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const usePrimaryConfiguration = (enabled = true) => {
  return useQuery<Configuration | null, Error>({
    queryKey: configurationQueryKeys.primary(),
    queryFn: () => configurationService.getPrimary(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

type PublicConfigurationQueryOverrides = Pick<
  UseQueryOptions<PublicConfiguration | null, Error>,
  "refetchInterval" | "staleTime" | "refetchOnWindowFocus"
>;

/**
 * Consumer nào cần cấu hình "tươi" hơn mặc định (ví dụ luồng POS phải bắt kịp
 * thay đổi trần điểm giữa phiên) truyền overrides riêng, các màn hình còn lại
 * giữ nguyên staleTime 5 phút.
 */
export const usePublicPrimaryConfiguration = (
  enabled = true,
  options?: PublicConfigurationQueryOverrides,
) => {
  return useQuery<PublicConfiguration | null, Error>({
    queryKey: configurationQueryKeys.publicPrimary(),
    queryFn: () => configurationService.getPublicPrimary(),
    enabled,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};
